import React, { useState } from 'react';
import { XMarkIcon, UserPlusIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import RoomSectionHeader from './RoomSectionHeader';
import MemberList from './MemberList';
import AttachmentsAccordion from './AttachmentsAccordion';
import type { ChatRoom } from '../../types/chat';
import { useCurrentChatIdentity } from '../../hooks/chat/useCurrentChatIdentity';

interface GroupInfoPanelProps {
  room: ChatRoom;
  onClose?: () => void;
  onAddMembers?: () => void;
  onEditGroup?: () => void;
  onRemoveMember?: (userId: string) => void;
}

/**
 * Right-rail panel for group rooms. Matches Figma `4029:196974`:
 * - Width 240 px, white background, left border
 * - Top bar with close X + small admin actions (icon-only) for room admins
 * - Two collapsible sections: "IN ROOM (n)" and "ATTACHMENTS (n)"
 * - No avatar/name banner — the chat header carries that affordance
 */
const GroupInfoPanel: React.FC<GroupInfoPanelProps> = ({
  room,
  onClose,
  onAddMembers,
  onEditGroup,
  onRemoveMember,
}) => {
  const { userId } = useCurrentChatIdentity();
  const selfUpper = (userId ?? '').toUpperCase();
  const me = room.members.find(
    (m) => (m.user_id ?? '').toUpperCase() === selfUpper
  );
  const canManage = !!me?.is_admin;
  const [membersOpen, setMembersOpen] = useState(true);

  return (
    <aside className="w-60 shrink-0 flex flex-col border-l border-border-secondary bg-surface-primary overflow-y-auto scrollbar-thin">
      <div className="flex items-center justify-end gap-1 px-2 pt-2">
        {canManage && onAddMembers && (
          <button
            type="button"
            onClick={onAddMembers}
            aria-label="Add members"
            className="p-1.5 rounded-full text-text-tertiary hover:text-primary-600 dark:hover:text-primary-350 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <UserPlusIcon className="w-4 h-4" />
          </button>
        )}
        {canManage && onEditGroup && (
          <button
            type="button"
            onClick={onEditGroup}
            aria-label="Edit group settings"
            className="p-1.5 rounded-full text-text-tertiary hover:text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
        )}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="p-1.5 rounded-full text-text-tertiary hover:text-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      <RoomSectionHeader
        label="In room"
        count={room.members.length}
        open={membersOpen}
        onToggle={() => setMembersOpen((p) => !p)}
      />
      {membersOpen && (
        <MemberList
          members={room.members}
          selfId={userId}
          canManage={canManage}
          onRemove={onRemoveMember}
        />
      )}

      <AttachmentsAccordion roomId={room.id} />
    </aside>
  );
};

export default GroupInfoPanel;
